import React, { useEffect, useState } from 'react';
import { Table, Button, Card, Typography, Modal, Form, Input, InputNumber, Switch, Space, message, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, PercentageOutlined, CheckCircleOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

const TaxProfiles = () => {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingProfile, setEditingProfile] = useState(null);
    const [form] = Form.useForm();

    const fetchProfiles = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/tax-profiles', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProfiles(res.data);
        } catch (err) {
            message.error('Failed to fetch tax profiles');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProfiles();
    }, []);

    const handleAdd = () => {
        setEditingProfile(null);
        form.resetFields();
        form.setFieldsValue({ is_default: false });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingProfile(record);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/tax-profiles/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            message.success('Tax profile deleted');
            fetchProfiles();
        } catch (err) {
            message.error(err.response?.data?.error || 'Failed to delete tax profile');
        }
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            if (editingProfile) {
                await axios.put(`/api/tax-profiles/${editingProfile.id}`, values, { headers });
                message.success('Tax profile updated');
            } else {
                await axios.post('/api/tax-profiles', values, { headers });
                message.success('Tax profile created');
            }
            setModalVisible(false);
            fetchProfiles();
        } catch (err) {
            message.error(err.response?.data?.error || 'Operation failed');
        }
    };

    const columns = [
        {
            title: 'Profile Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <Space>
                    <Text strong>{text}</Text>
                    {record.is_default && <Tag color="green"><CheckCircleOutlined /> Default</Tag>}
                </Space>
            )
        },
        {
            title: 'Rate (%)',
            dataIndex: 'rate',
            key: 'rate',
            render: (text) => <Tag color="blue">{text}%</Tag>
        },
        {
            title: 'Created At',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => new Date(text).toLocaleDateString()
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>Edit</Button>
                    {!record.is_default && (
                        <Popconfirm title="Delete this profile?" onConfirm={() => handleDelete(record.id)}>
                            <Button icon={<DeleteOutlined />} danger>Delete</Button>
                        </Popconfirm>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div style={{ padding: '0 0px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                    <Title level={4} style={{ margin: 0 }}><PercentageOutlined /> Tax Profiles</Title>
                    <Text type="secondary">Manage standard tax rates used across your products.</Text>
                </div>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>Add Profile</Button>
            </div>

            <Card bodyStyle={{ padding: 0 }}>
                <Table
                    dataSource={profiles}
                    columns={columns}
                    loading={loading}
                    rowKey="id"
                    pagination={false}
                />
            </Card>

            <Modal
                title={editingProfile ? 'Edit Tax Profile' : 'Create Tax Profile'}
                open={modalVisible}
                onOk={handleModalOk}
                onCancel={() => setModalVisible(false)}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Profile Name" rules={[{ required: true }]}>
                        <Input placeholder="e.g. Jordan Standard VAT" />
                    </Form.Item>
                    <Form.Item name="rate" label="Tax Rate (%)" rules={[{ required: true }]}>
                        <InputNumber min={0} max={100} style={{ width: '100%' }} precision={2} addonAfter="%" />
                    </Form.Item>
                    <Form.Item name="is_default" label="Set as Default" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default TaxProfiles;
